import { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';

// Utils
import { cn } from 'utils';

// Components
import PageLayout from '../PageLayout/PageLayout';
import BreadCrumb from '../../atoms/BreadCrumb/BreadCrumb';
import Button from '../../atoms/Button/Button';
import CardEvent from '../../molecules/CardEvent/CardEvent';
// Utils & misc
import { pageSlugToTitle } from '../../../src/utils/pageSlug';
import { EVENTS_SLUG, LOCALE_EN } from '../../../src/constants';
// Styles
import styles from './EventsLayout.module.css';

const filterPastEvents = (events) => (events ? events.filter(
  (event) => {
    const todayTimestamp = new Date().getTime();
    const endDateTimestamp = new Date(event?.data?.endDate).getTime() + 86400000;

    return endDateTimestamp >= todayTimestamp;
  },
) : []);

const sortEventsByEndDate = (events) => (events ? [...events].sort(
  (a, b) => new Date(a?.data?.endDate).getTime() - new Date(b?.data?.endDate).getTime(),
) : []);

function EventsLayout({
  categories, header, footer, events, onCategoryClick, selectedCategory,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) {
  // Hooks
  const { t } = useTranslation();
  // States
  const categoriesList = useMemo(
    () => categories.map((categoryData) => categoryData?.data?.tag),
    [categories],
  );
  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: '/',
    },
    {
      name: pageSlugToTitle(EVENTS_SLUG),
      position: 2,
      item: null,
    },
  ];

  const slugs = {
    [LOCALE_EN]: null,
  };

  const loadMoreRef = useRef(null);

  const processedEvents = useMemo(
    () => sortEventsByEndDate(filterPastEvents(events)),
    [events],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isLoadingMore, onLoadMore]);

  return (
    <>
      <Head>
        <title>{t('events.metaTitle')}</title>
        <meta name="description" content={t('events.metaDescription')} />
      </Head>

      <PageLayout
        header={header}
        footer={footer}
        slugs={slugs}
        theme="white"
      >
        <div className={styles.content}>
          <h1 className={styles.title}>{t('events.title')}</h1>

          <div className={styles.categories}>
            <Button
              key="all"
              className={cn([styles.btn, selectedCategory?.name === 'all' ? styles.active : ''])}
              type="button"
              onClick={() => onCategoryClick('all')}
              outline={selectedCategory?.name !== 'all'}
            >
              {t('events.allEventsLabel')}
            </Button>
            {categoriesList?.map(((category) => (
              <Button
                key={category}
                className={cn([styles.btn, selectedCategory?.name === category ? styles.active : ''])}
                type="button"
                onClick={() => onCategoryClick(category)}
                outline={selectedCategory?.name !== category}
              >
                {category}
              </Button>
            )))}
          </div>

          <div className={styles['content-grid']}>
            {processedEvents?.map((event) => (
              <CardEvent
                key={event?.id}
                title={event?.data?.title}
                subtitle={event?.data?.subtitle}
                startDate={event?.data?.startDate}
                endDate={event?.data?.endDate}
                hoursInfo={event?.data?.hoursInfo}
                description={event?.data?.description}
                media={event?.data?.media}
                ctaLabel={event?.data?.ctaLabel}
                ctaUrl={event?.data?.ctaUrl}
              />
            ))}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={loadMoreRef} className={styles.loadingMore}>
            {isLoadingMore ? (
              <p>{t('events.eventsLoading')}</p>
            ) : null}
          </div>

          <BreadCrumb noPadding items={breadcrumbItems} theme="transparent" />
        </div>
      </PageLayout>
    </>
  );
}

EventsLayout.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        category: PropTypes.string,
      }),
      contentId: PropTypes.string,
    }),
  ).isRequired,
  header: PropTypes.shape({}).isRequired,
  footer: PropTypes.shape({}).isRequired,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({}),
    }),
  ).isRequired,

  onCategoryClick: PropTypes.func.isRequired,
  selectedCategory: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
  }),
  hasNextPage: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};

EventsLayout.defaultProps = {
  selectedCategory: {
    name: 'all',
    id: null,
  },
  hasNextPage: false,
  isLoadingMore: false,
  onLoadMore: () => {},
};

export default EventsLayout;
